import TimelyHomePage from '../../apps/timely/TimelyHomePage';
import { getLoginDestination } from '../util/authUtils';
import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isConciergeUser } from "../util/conciergeAccess";
import {
  buildHttpsUrl,
  getPublicHostForVertical,
  shouldCrossDomainNavigate,
} from "../util/brandDomains";
import {
  getRuntimeBusinessHomePath,
  getRuntimePublicHomePath,
  isTimelyRuntime,
} from "../runtime/appProfile";

const HOST_ROOT_REDIRECTS = {
  "care.plany365.com": "/find",
  "agents.plany365.com": "/care/agent",
  "partners.plany365.com": "/referrer",
  "carejobs.plany365.com": "/care/jobs/find",
};
const PROVIDER_BRAND_HOSTS = new Set(["providers.plany365.com"]);

function getNormalizedHostname() {
  if (typeof window === "undefined") return "";
  return String(window.location.hostname || "").trim().toLowerCase();
}

function isBookingBrandHost() {
  return isTimelyRuntime(getNormalizedHostname());
}

function isProviderBrandHost() {
  return PROVIDER_BRAND_HOSTS.has(getNormalizedHostname());
}

function getHostRootRedirectPath() {
  return HOST_ROOT_REDIRECTS[getNormalizedHostname()] || null;
}

/**
 * RootRedirect
 * ---------------------------------------------------
 * Priority order:
 *
 * 0) Logged-in provider WITH business → biz dashboard (manageUrl)
 *    (unless explicit customer intent)
 *
 * 1) ?v=care|agent|rentals|talent (URL override)
 * 2) localStorage "lastVertical"
 * 3) fallback -> "/choose"
 */
export default function RootRedirect({ providerUser }) {
  const nav = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qV = params.get("v"); // "care" | "agent" | "rentals" | "talent"

    const isLoggedIn = !!providerUser?.userId;
    const hasBusiness = !!providerUser?.defaultBusinessId;
    const isAgent = isConciergeUser(providerUser);
    const bookingBrandHost = isBookingBrandHost();
    const providerBrandHost = isProviderBrandHost();
    const hostRootRedirectPath = getHostRootRedirectPath();

    // --- Explicit customer intent detection ---
    const isCustomerIntent =
      params.get("customer") === "1" ||
      location.state?.fromCustomer === true ||
      location.pathname.startsWith("/care") ||
      location.pathname.startsWith("/b/");

    if (qV && !bookingBrandHost) {
      localStorage.setItem("lastVertical", qV);
      const targetHost = getPublicHostForVertical(qV);
      if (shouldCrossDomainNavigate(targetHost)) {
        window.location.assign(buildHttpsUrl(targetHost, "/"));
        return;
      }
      nav(`/${qV}`, { replace: true });
      return;
    }

    if (bookingBrandHost) {
      localStorage.setItem("lastVertical", "booking");
      if (isLoggedIn && !isCustomerIntent) {
        nav(getLoginDestination(providerUser), { replace: true });
        return;
      }
      return;
    }

    if (isLoggedIn && isAgent && !isCustomerIntent) {
      nav("/care/agent", { replace: true });
      return;
    }

    if (providerBrandHost) {
      if (isLoggedIn && hasBusiness && !isCustomerIntent) {
        nav(getRuntimeBusinessHomePath(), { replace: true });
        return;
      }
      nav("/login", { replace: true });
      return;
    }

    if (hostRootRedirectPath) {
      nav(hostRootRedirectPath, { replace: true });
      return;
    }

    if (isLoggedIn && hasBusiness && !isCustomerIntent) {
      nav(getRuntimeBusinessHomePath(), { replace: true });
      return;
    }

    nav(getRuntimePublicHomePath(), { replace: true });
  }, [nav, location, providerUser]);

  return isBookingBrandHost() ? <TimelyHomePage /> : null;
}
